import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/layout/Layout';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Loader2, Phone, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getText, staticTexts } from '../utils/translations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EquipmentPage = () => {
  const { language } = useLanguage();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageIndex, setImageIndex] = useState(0);

  const txt = staticTexts.equipment;

  // Helper to get category name based on language
  const getCategoryName = (cat) => {
    if (language === 'ar' && cat.name_ar) return cat.name_ar;
    if (language === 'fr' && cat.name?.fr) return cat.name.fr;
    if (language === 'en' && cat.name?.en) return cat.name.en;
    return cat.name?.fr || cat.name?.en || cat.name_fr || cat.name_en || '';
  };

  // Helper to get category description based on language
  const getCategoryDescription = (cat) => {
    if (language === 'ar' && cat.description_ar) return cat.description_ar;
    if (language === 'fr' && cat.description?.fr) return cat.description.fr;
    if (language === 'en' && cat.description?.en) return cat.description.en;
    return cat.description?.fr || cat.description?.en || cat.description_fr || cat.description_en || '';
  };

  // Helper to get part name based on language
  const getPartName = (part) => {
    if (language === 'ar' && part.name_ar) return part.name_ar;
    if (language === 'fr' && part.name_fr) return part.name_fr;
    if (language === 'en' && part.name_en) return part.name_en;
    return part.name_fr || part.name_en || '';
  };

  // Helper to get part description based on language
  const getPartDescription = (part) => {
    if (language === 'ar' && part.description_ar) return part.description_ar;
    if (language === 'fr' && part.description_fr) return part.description_fr;
    if (language === 'en' && part.description_en) return part.description_en;
    return part.description_fr || part.description_en || '';
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/equipment-categories`)
      .then(res => res.json())
      .then(data => {
        if (data && data.length > 0) {
          const transformed = data.map(cat => ({
            id: cat.id,
            name: { en: cat.name_en, fr: cat.name_fr },
            name_ar: cat.name_ar,
            description: { en: cat.description_en || '', fr: cat.description_fr || '' },
            description_ar: cat.description_ar,
            images: cat.images || [],
            parts: cat.parts || []
          }));
          setCategories(transformed);
          setSelectedCategory(transformed[0]);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Get all images for lightbox navigation (from parts or legacy images)
  const getAllImages = (category) => {
    if (category.parts && category.parts.length > 0) {
      return category.parts.map(p => p.image).filter(Boolean);
    }
    return category.images || [];
  };

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setImageIndex(index);
  };

  const nextImage = () => {
    if (selectedCategory) {
      const images = getAllImages(selectedCategory);
      const nextIdx = (imageIndex + 1) % images.length;
      setImageIndex(nextIdx);
      setSelectedImage(images[nextIdx]);
    }
  };

  const prevImage = () => {
    if (selectedCategory) {
      const images = getAllImages(selectedCategory);
      const prevIdx = (imageIndex - 1 + images.length) % images.length;
      setImageIndex(prevIdx);
      setSelectedImage(images[prevIdx]);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  // Determine display mode: parts with descriptions or legacy images
  const hasParts = selectedCategory?.parts && selectedCategory.parts.length > 0;
  const displayItems = hasParts ? selectedCategory.parts : (selectedCategory?.images || []).map((img, i) => ({ image: img, id: i }));

  return (
    <Layout>
      {/* Page Header */}
      <section className="pt-20 pb-16 bg-slate-100">
        <div className="container mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-4">
            {getText(language, txt.ourProducts.en, txt.ourProducts.fr, txt.ourProducts.ar)}
          </p>
          <h1 className="text-5xl md:text-6xl font-light text-slate-800">
            {getText(language, txt.title.en, txt.title.fr, txt.title.ar)}
          </h1>
        </div>
      </section>

      {/* Equipment Categories - Tab/Card View */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-6">
          {/* Category Cards Grid (Tabs) */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {categories.map((cat) => {
              const firstImage = cat.parts?.[0]?.image || cat.images?.[0];
              const itemCount = cat.parts?.length || cat.images?.length || 0;
              
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat)}
                  className={`relative overflow-hidden rounded-2xl aspect-[4/3] group transition-all duration-300 ${
                    selectedCategory?.id === cat.id 
                      ? 'ring-4 ring-slate-800 shadow-xl scale-[1.02]' 
                      : 'hover:shadow-lg'
                  }`}
                >
                  {/* Background Image */}
                  <img
                    src={firstImage || 'https://via.placeholder.com/400x300?text=No+Image'}
                    alt={getCategoryName(cat)}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  
                  {/* Overlay */}
                  <div className={`absolute inset-0 transition-all duration-300 ${
                    selectedCategory?.id === cat.id 
                      ? 'bg-slate-900/70' 
                      : 'bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent group-hover:bg-slate-900/60'
                  }`} />
                  
                  {/* Content */}
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <h3 className="text-xl font-semibold text-white mb-1">
                      {getCategoryName(cat)}
                    </h3>
                    <p className="text-sm text-white/70">
                      {itemCount} {getText(language, 'parts', 'pièces', 'قطع')}
                    </p>
                  </div>
                  
                  {/* Selected indicator */}
                  {selectedCategory?.id === cat.id && (
                    <div className="absolute top-4 right-4 w-3 h-3 bg-white rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Selected Category Content */}
          {selectedCategory && (
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              {/* Header with title, description and quote button */}
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8 pb-6 border-b border-slate-100">
                <div className="flex-1">
                  <h2 className="text-3xl font-light text-slate-800 mb-2">
                    {getCategoryName(selectedCategory)}
                    <span className="text-sm text-slate-400 ml-3 font-normal">
                      ({displayItems.length} {getText(language, 'parts', 'pièces', 'قطع')})
                    </span>
                  </h2>
                  
                  {/* Category Description */}
                  {getCategoryDescription(selectedCategory) && (
                    <p className="text-slate-500 leading-relaxed max-w-2xl">
                      {getCategoryDescription(selectedCategory)}
                    </p>
                  )}
                </div>
                
                {/* Quote Button */}
                <Link to="/contact" className="flex-shrink-0">
                  <Button className="bg-slate-800 hover:bg-slate-900 text-white">
                    <Phone className="w-4 h-4 mr-2" />
                    {getText(language, 'Request Quote', 'Demander un Devis', 'طلب عرض سعر')}
                  </Button>
                </Link>
              </div>
              
              {/* Parts List View */}
              {displayItems.length > 0 ? (
                <div className="space-y-4">
                  {displayItems.map((item, index) => {
                    const partName = getPartName(item);
                    const partDesc = getPartDescription(item);
                    const hasContent = partName || partDesc;
                    
                    return (
                      <div 
                        key={item.id || index}
                        className="flex gap-6 p-4 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        {/* Image - Left Side */}
                        <div 
                          className="w-40 h-40 flex-shrink-0 rounded-xl overflow-hidden cursor-pointer group bg-slate-200"
                          onClick={() => openLightbox(item.image, index)}
                        >
                          {item.image ? (
                            <>
                              <img
                                src={item.image}
                                alt={partName || `${getCategoryName(selectedCategory)} ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                              />
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              {getText(language, 'No image', 'Pas d\'image', 'لا توجد صورة')}
                            </div>
                          )}
                        </div>
                        
                        {/* Content - Right Side */}
                        <div className="flex-1 flex flex-col justify-center">
                          {hasContent ? (
                            <>
                              {partName && (
                                <h4 className="text-lg font-medium text-slate-800 mb-2">
                                  {partName}
                                </h4>
                              )}
                              {partDesc && (
                                <p className="text-slate-600 leading-relaxed">
                                  {partDesc}
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-slate-400 italic">
                              {getText(language, 
                                'Quality spare parts from renowned brands.', 
                                'Pièces de rechange de qualité des marques renommées.', 
                                'قطع غيار عالية الجودة من العلامات التجارية المشهورة.'
                              )}
                            </p>
                          )}
                          
                          {/* Individual quote link */}
                          <Link 
                            to="/contact" 
                            className="mt-3 text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 w-fit"
                          >
                            <Phone className="w-3 h-3" />
                            {getText(language, 'Ask about this part', 'Renseignement sur cette pièce', 'استفسار عن هذه القطعة')}
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  {getText(language, 
                    'No parts available for this category.', 
                    'Aucune pièce disponible pour cette catégorie.', 
                    'لا توجد قطع متاحة لهذه الفئة.'
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Image Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-2 bg-black border-none">
          <DialogTitle className="sr-only">Image preview</DialogTitle>
          {selectedImage && selectedCategory && (
            <div className="relative">
              <img
                src={selectedImage}
                alt="Equipment"
                className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
              />
              
              {/* Close button */}
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              
              {getAllImages(selectedCategory).length > 1 && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 hover:bg-white/40 rounded-full transition-colors"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/60 rounded-full text-white text-sm">
                    {imageIndex + 1} / {getAllImages(selectedCategory).length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EquipmentPage;
